import { AbstractService } from '@/services/core/serviceFactory';
import { usersGraphqlService, patientsGraphqlService } from '@/services/api';
import { PATIENT_LIST_CLINIC_IDS } from '@/config/envConfig';

export interface PatientEnrollerAssignment {
  clinic_ids: number[];
}

export interface PatientEnrollerResponse {
  pep_patient_enroller_active_assignments: PatientEnrollerAssignment[];
}

export interface PEStatusData {
  deferred_task: boolean;
  follow_up: boolean;
  patient_enroller_id: number;
}

export interface PEStatusResponse {
  pep_vw_patient_enroller_list: PEStatusData[];
}

export interface PEPatient {
  pep_patient_id: number;
  patient_clinic_data_id: number;
  full_name: string;
  dob: string;
  clinic_id: number;
  clinic_name: string;
  provider_name: string;
  patient_clinic_timezone: string;
  phone_number: string;
  consented_appointment_date_time: string;
  ccm_status_value: string;
  rpm_status_value: string;
}

export interface PEPatientListResponse {
  pep_vw_patient_enroller_patient_list: PEPatient[];
}

export interface PETaskPatientListResponse {
  pep_vw_patient_enroller_task_patient_list: PEPatient[];
}

export interface CreatePatientEnrollerPayload {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  clinic_ids: number[];
  deferred_task: string;
  follow_up_task: string;
}

export interface CreatePatientEnrollerResponse {
  success: boolean;
  message?: string;
  data?: any;
}

export interface UpdatePatientEnrollerPayload {
  first_name: string;
  last_name: string;
  email: string;
  password?: string;
  clinic_ids: number[];
  deferred_task: boolean;
  follow_up_task: boolean;
}

export interface UpdatePatientEnrollerResponse {
  success: boolean;
  message?: string;
  data?: any;
}

export class PatientEnrollerService extends AbstractService {
  /**
   * Fetch clinic IDs for a patient enroller
   */
  async fetchClinicIds(patientEnrollerId: number): Promise<{ success: boolean; data?: PatientEnrollerResponse; errors?: string[] }> {
    this.requireAuth();

    if (!patientEnrollerId) {
      throw new Error('Patient Enroller ID is required');
    }

    try {
      const result = await usersGraphqlService.getPatientEnrollerClinicIds(this.context.hasuraToken!, patientEnrollerId);
      return {
        success: true,
        data: { pep_patient_enroller_active_assignments: result.data?.pep_patient_enroller_active_assignments || [] }
      };
    } catch (error) {
      console.error('Error fetching PE clinic IDs:', error);
      return {
        success: false,
        errors: ['Failed to fetch clinic IDs']
      };
    }
  }

  /**
   * Fetch patient enroller status (deferred task and follow up flags)
   */
  async fetchPEStatus(patientEnrollerId: number): Promise<{ success: boolean; data?: PEStatusResponse; errors?: string[] }> {
    this.requireAuth();

    if (!patientEnrollerId) {
      throw new Error('Patient Enroller ID is required');
    }

    try {
      const result = await usersGraphqlService.getPatientEnrollerStatus(this.context.hasuraToken!, patientEnrollerId);
      return {
        success: true,
        data: { pep_vw_patient_enroller_list: result.data?.pep_vw_patient_enroller_list || [] } as unknown as { pep_vw_patient_enroller_list: PEStatusData[] }
      };
    } catch (error) {
      console.error('Error fetching PE status:', error);
      return {
        success: false,
        errors: ['Failed to fetch PE status']
      };
    }
  }

  /**
   * Fetch patient enroller patient list by clinic IDs
   */
  async fetchPEPatientList(clinicIds: number[]): Promise<{ success: boolean; data?: PEPatientListResponse; errors?: string[] }> {
    this.requireAuth();

    if (!clinicIds || clinicIds.length === 0) {
      throw new Error('Clinic IDs are required');
    }

    try {
      const result = await patientsGraphqlService.getPatientEnrollerPatientList(this.context.hasuraToken!, clinicIds);
      return {
        success: true,
        data: result.data,
        pep_vw_patient_enroller_patient_list: result.data?.pep_vw_patient_enroller_patient_list || []
      } as any;
    } catch (error) {
      console.error('Error fetching PE patient list:', error);
      return {
        success: false,
        errors: ['Failed to fetch PE patient list']
      };
    }
  }

  /**
   * Fetch patient list with special clinic IDs (for general patient list)
   * Used by usePatientList hook - uses clinic IDs from environment config
   */
  async fetchPatientListWithSpecialClinicIds(): Promise<PEPatientListResponse> {
    this.requireAuth();

    try {
      const result = await patientsGraphqlService.getPatientEnrollerPatientList(this.context.hasuraToken!, [...PATIENT_LIST_CLINIC_IDS]);
      return { pep_vw_patient_enroller_patient_list: result.data?.pep_vw_patient_enroller_patient_list || [] } as unknown as PEPatientListResponse;
    } catch (error) {
      console.error('Error fetching patient list with special clinic IDs:', error);
      throw new Error('Failed to fetch patient list');
    }
  }

  /**
   * Process clinic IDs from API response - removes duplicates and flattens arrays
   */
  processClinicIds(assignments: PatientEnrollerAssignment[]): number[] {
    const allClinicIds: number[] = assignments.flatMap((assignment) => assignment.clinic_ids || []);
    return [...new Set(allClinicIds)]; // Remove duplicates
  }

  /**
   * Filter patients by RPM status value
   */
  getPatientsByRPMStatus(patients: PEPatient[], status: string): PEPatient[] {
    return patients.filter(patient => patient.rpm_status_value === status);
  }

  /**
   * Filter patients by search criteria
   */
  filterPatients(patients: PEPatient[], searchBy: string, searchTerm: string): PEPatient[] {
    if (!searchTerm) return patients;

    const searchTermLower = searchTerm.toLowerCase();
    
    return patients.filter((patient: PEPatient) => {
      switch (searchBy) {
        case 'Name':
          return patient.full_name?.toLowerCase().includes(searchTermLower);
        case 'DOB':
          return patient.dob?.includes(searchTerm);
        case 'Phone Number':
          return patient.phone_number?.includes(searchTerm);
        case 'Provider Name':
          return patient.provider_name?.toLowerCase().includes(searchTermLower);
        case 'Appointment Date':
          return patient.consented_appointment_date_time?.includes(searchTerm);
        case 'Clinic Name':
          return patient.clinic_name?.toLowerCase().includes(searchTermLower);
        case 'CCM Status':
          if (searchTerm.includes(',')) {
            // Multiple statuses selected
            const statuses = searchTerm.split(',').map(s => s.trim().toLowerCase());
            return statuses.some(status => patient.ccm_status_value?.toLowerCase().includes(status));
          }
          return patient.ccm_status_value?.toLowerCase().includes(searchTermLower);
        case 'RPM Status':
          return patient.rpm_status_value?.toLowerCase().includes(searchTermLower);
        default:
          return (
            patient.full_name?.toLowerCase().includes(searchTermLower) ||
            patient.dob?.includes(searchTerm) ||
            patient.phone_number?.includes(searchTerm) ||
            patient.provider_name?.toLowerCase().includes(searchTermLower) ||
            patient.clinic_name?.toLowerCase().includes(searchTermLower)
          );
      }
    });
  }

  /**
   * Create a new patient enroller
   */
  async createPatientEnroller(payload: CreatePatientEnrollerPayload): Promise<CreatePatientEnrollerResponse> {
    this.requireAuth();
    this.requireUserId();

    try {
      const response = await this.apiClient.post(`/api/pep/${this.context.userId}/pa`, payload, this.context.token);
      return {
        success: true,
        data: response.data,
        message: 'Patient enroller created successfully'
      };
    } catch (error) {
      console.error('Error creating patient enroller:', error);
      
      // Provide more specific error messages
      if (error instanceof Error) {
        if (error.message.includes('401') || error.message.includes('403')) {
          throw new Error("Authentication failed. Please check your credentials and try again.");
        } else if (error.message.includes('400')) {
          throw new Error("Invalid information provided. Please check all fields and try again.");
        } else if (error.message.includes('409')) {
          throw new Error("Email address already exists. Please use a different email.");
        } else if (error.message.includes('500')) {
          throw new Error("Server error. Please try again later.");
        }
      }
      
      throw error;
    }
  }

  /**
   * Update an existing patient enroller
   */
  async updatePatientEnroller(
    patientEnrollerId: number,
    payload: UpdatePatientEnrollerPayload
  ): Promise<UpdatePatientEnrollerResponse> {
    this.requireAuth();
    this.requireUserId();

    if (!patientEnrollerId) {
      throw new Error('Patient Enroller ID is required');
    }

    try {
      const response = await this.apiClient.put(`/api/pep/${this.context.userId}/pa/${patientEnrollerId}`, payload, this.context.token);
      return {
        success: true,
        data: response.data,
        message: 'Patient enroller updated successfully'
      };
    } catch (error) {
      console.error('Error updating patient enroller:', error);
      
      // Provide more specific error messages
      if (error instanceof Error) {
        if (error.message.includes('401') || error.message.includes('403')) {
          throw new Error("Authentication failed. Please check your credentials and try again.");
        } else if (error.message.includes('400')) {
          throw new Error("Invalid information provided. Please check all fields and try again.");
        } else if (error.message.includes('404')) {
          throw new Error("Patient enroller not found.");
        } else if (error.message.includes('500')) {
          throw new Error("Server error. Please try again later.");
        }
      }
      
      throw error;
    }
  }

  /**
   * Fetch patient enroller task patient list with search filters
   * Used by PEPatientListSearchable component for follow-up and deferred patients
   */
  async fetchPETaskPatientList(
    clinicIds: number[],
    statusFilter: string,
    searchFilters?: {
      searchBy?: string;
      searchTerm?: string;
      selectedDate?: Date;
    }
  ): Promise<{ success: boolean; data?: PETaskPatientListResponse; errors?: string[] }> {
    this.requireAuth();

    if (!clinicIds || clinicIds.length === 0) {
      throw new Error('Clinic IDs are required');
    }

    if (!statusFilter) {
      throw new Error('Status filter is required');
    }

    try {
      const result = await patientsGraphqlService.getPatientEnrollerTaskPatientList(
        this.context.hasuraToken!,
        clinicIds,
        statusFilter,
        '' // Remove complex whereConditions, use simplified query
      );
      
      return {
        success: true,
        data: { pep_vw_patient_enroller_task_patient_list: result.data?.pep_vw_patient_enroller_task_patient_list || [] }
      };
    } catch (error) {
      console.error('Error fetching PE task patient list:', error);
      return {
        success: false,
        errors: ['Failed to fetch PE task patient list']
      };
    }
  }

  /**
   * Delete a patient enroller
   */
  async deletePatientEnroller(patientEnrollerId: number): Promise<CreatePatientEnrollerResponse> {
    this.requireAuth();
    this.requireUserId();

    if (!patientEnrollerId) {
      throw new Error('Patient Enroller ID is required');
    }

    try {
      const response = await this.apiClient.delete(`/api/pep/${this.context.userId}/pa/${patientEnrollerId}`, this.context.token);
      return {
        success: true,
        data: response.data,
        message: 'Patient enroller deleted successfully'
      };
    } catch (error) {
      console.error('Error deleting patient enroller:', error);
      throw error;
    }
  }
}